import React, { useState, useEffect } from 'react';
import styles from './FilterSalesPage.module.css';
import axios from 'axios';
import { API_BASE_URL } from '../Config.js';
import SalesReport from './SalesReport';
import SalesExportModal from './SalesExportModal';
import {
  CalendarRange,
  ChartColumn,
  Check,
  Download,
  PackageSearch,
  RefreshCw,
  School,
  Shirt,
  SlidersHorizontal,
  TriangleAlert,
  X,
} from 'lucide-react';

const FILTER_TYPES = [
  { key: 'dateRange', label: 'Date range', hint: 'Sales between two dates', icon: CalendarRange },
  { key: 'school', label: 'School', hint: 'Sales for one school', icon: School },
  { key: 'item', label: 'Item', hint: 'Sales for one item type', icon: Shirt },
];

const FilterSalesPage = () => {
  // ── NEW: Tab state ────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('view'); // 'view' | 'export'
  const [showExportModal, setShowExportModal] = useState(false);
  // ─────────────────────────────────────────────────────────────────────────

  const [filters, setFilters] = useState({
    dateRange: false,
    item: false,
    school: false,
  });

  const [selectedFilters, setSelectedFilters] = useState({
    dateRange: { startDate: '', endDate: '' },
    item: '',
    school: '',
  });

  const [schools, setSchools] = useState([]);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(true);
  // UI only: tells "nothing loaded yet" apart from "loaded, no sales found"
  const [hasSearched, setHasSearched] = useState(false);

  const handleApplyFilters = () => {
    setHasSearched(true);
    setSalesData([]);
    fetchSalesData();
  };

  const clearFilter = (filterType) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: false
    }));

    if (filterType === 'dateRange') {
      setSelectedFilters(prev => ({
        ...prev,
        dateRange: { startDate: '', endDate: '' }
      }));
    } else {
      setSelectedFilters(prev => ({
        ...prev,
        [filterType]: ''
      }));
    }

    // Clear error message when filters are cleared
    setErrorMessage('');
  };

  const clearAllFilters = () => {
    setFilters({
      dateRange: false,
      item: false,
      school: false
    });

    setSelectedFilters({
      dateRange: { startDate: '', endDate: '' },
      item: '',
      school: ''
    });

    setSalesData([]);
    setErrorMessage('');
  };

  // Close the filter modal on Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showFilterModal) {
        setShowFilterModal(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showFilterModal]);

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: checked,
    }));

    // Reset the selections when filters are changed
    if (name === 'school' && !checked) {
      setSelectedFilters((prevSelected) => ({
        ...prevSelected,
        school: '',
      }));
    }
    if (name === 'item' && !checked) {
      setSelectedFilters((prevSelected) => ({
        ...prevSelected,
        item: '',
      }));
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDropdownChange = (event) => {
    const { name, value } = event.target;
    setSelectedFilters((prevSelected) => ({
      ...prevSelected,
      [name]: value,
    }));
    if (name === 'school') {
      setErrorMessage(''); // Clear error when a school is selected
    }
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setSelectedFilters((prevSelected) => ({
      ...prevSelected,
      dateRange: {
        ...prevSelected.dateRange,
        [name]: value,
      },
    }));
  };

  // Fetch schools when the school filter is selected
  useEffect(() => {
    if (filters.school) {
      const userData = JSON.parse(sessionStorage.getItem('user'));
      const storeId = userData?.storeId; // Retrieve storeId from user data
      if (storeId) {
        // Make API call with storeId as a query parameter
        axios.get(`${API_BASE_URL}/user/filter/getSchool`, {
          params: { storeId: storeId }
        })
        .then(response => {
          setSchools(response.data);
        })
        .catch(error => {
          console.error('Error fetching schools:', error);
        });
      } else {
        console.error('Store ID not found in user data');
      }
    }
  }, [filters.school]);

  // Fetch items when the item filter is selected and populate based on school code
  useEffect(() => {
    if (filters.item) {

      const userData = JSON.parse(sessionStorage.getItem('user'));
      const storeId = userData?.storeId; // Retrieve storeId from user data

      if (!storeId) {
        console.error('Store ID not found in user data');
        return;
      }

      if (selectedFilters.school) {
        axios.get(`${API_BASE_URL}/user/filter/school/item_type`, {
          params: {
            schoolCode: selectedFilters.school,
            storeId: storeId
          }
        })
        .then(response => {
          setFilteredItems(response.data);
        })
        .catch(error => {
          console.error('Error fetching filtered items:', error);
        });

      // If no school is selected, show an error message
      } else if (filters.item && filters.school && !selectedFilters.school) {
        setErrorMessage('Please select a school first.');
      } else {
        axios.get(`${API_BASE_URL}/user/filter/item_type`, {
          params: { storeId: storeId }
        })
        .then(response => {
          setItems(response.data);
        })
        .catch(error => {
          console.error('Error fetching items:', error);
        });
      }
    }
  }, [filters.item, selectedFilters.school]);

  const fetchSalesData = () => {
    setLoading(true);
    let url = `${API_BASE_URL}/report/findReportAll`; // Default URL for all reports
    const params = {};
    const user = JSON.parse(sessionStorage.getItem("user"));
    const storeId = user ? user.storeId : '';

    if (storeId) {
      params.storeId = storeId;
    }

    // Check if Date Range filter is selected
    if (filters.dateRange) {
      params.startDate = selectedFilters.dateRange.startDate;
      params.endDate = selectedFilters.dateRange.endDate;

    }

    // Check if School filter is selected
    if (filters.school && selectedFilters.school) {
      params.schoolCode = selectedFilters.school;
    }

    // Check if Item filter is selected
    if (filters.item && selectedFilters.item) {
      params.itemCode = selectedFilters.item;
    }

    // Determine the URL based on the filters
    if (filters.dateRange && filters.school && filters.item) {
      url = `${API_BASE_URL}/sales/report/school_item_type_date`;
      console.log("Filter 1");
    } else if (filters.dateRange && filters.school) {
      url = `${API_BASE_URL}/sales/report/school_code_and_date`;
      console.log("Filter 2");
    } else if (filters.dateRange && filters.item) {
      url = `${API_BASE_URL}/sales/report/item_code_and_date`;
      console.log("Filter 3");
    } else if (filters.school && filters.item) {
      url = `${API_BASE_URL}/sales/report/school_and_item_type`;
      console.log("Filter 4");
    } else if (filters.dateRange) {
      url = `${API_BASE_URL}/sales/report/sales_date`;
      console.log("Filter 5");
    } else if (filters.school) {
      url = `${API_BASE_URL}/sales/report/school_code`;
      console.log("Filter 6");
    } else if (filters.item) {
      url = `${API_BASE_URL}/sales/report/item_code`;
      console.log("Filter 7");
    }

    // Make the API call
    if (Object.keys(params).length > 0) {
      axios.get(url, { params })
        .then(response => {
          setSalesData(response.data);
          setLoading(false);

          if (response.data.length > 0) {
            setShowFilterModal(false);
          }

        })
        .catch(error => {
          console.error('Error fetching sales data:', error);
          setLoading(false);
        }).finally(() => {
          setLoading(false); // Re-enable button after response
        });
    } else {
      // If no filters are applied, fetch all sales data
      axios.get(url)
        .then(response => {
          setSalesData(response.data);

          if (response.data.length > 0) {
            setShowFilterModal(false);
          }

          setLoading(false); // Re-enable button after response
        })
        .catch(error => {
          console.error('Error fetching sales data:', error);
          setLoading(false); // Re-enable button after response
        }).finally(() => {
          setLoading(false); // Re-enable button after response
        });
    }

  };

  const hasDateChip = filters.dateRange && selectedFilters.dateRange.startDate;
  const hasSchoolChip = filters.school && selectedFilters.school;
  const hasItemChip = filters.item && selectedFilters.item;
  const activeFilterCount = [hasDateChip, hasSchoolChip, hasItemChip].filter(Boolean).length;
  const noneTicked = !filters.dateRange && !filters.school && !filters.item;
  const closeFilters = () => setShowFilterModal(false);

  return (
    <>
      {/* Filter modal — rendered outside the page so it covers the whole dashboard */}
      {showFilterModal && (
        <div className={styles.modalOverlay} onClick={closeFilters}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="sales-filter-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <h3 id="sales-filter-title" className={styles.modalTitle}>
                  Filter sales report
                </h3>
                <p className={styles.modalSubtitle}>
                  Tick Date range, School, Item or any mix. Leave all unticked to load every sale.
                </p>
              </div>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={closeFilters}
                aria-label="Close filters"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div role="group" aria-labelledby="sales-filter-by">
                <p id="sales-filter-by" className={styles.sectionLabel}>
                  Filter by
                </p>
                <div className={styles.checkGrid}>
                  {FILTER_TYPES.map(({ key, label, hint, icon: Icon }) => {
                    const checked = filters[key];
                    return (
                      <label
                        key={key}
                        className={`${styles.checkCard} ${checked ? styles.checkCardOn : ''}`}
                      >
                        <input
                          type="checkbox"
                          className={styles.checkInput}
                          name={key}
                          checked={checked}
                          onChange={handleCheckboxChange}
                        />
                        <span className={styles.checkBox} aria-hidden="true">
                          {checked && <Check size={13} strokeWidth={3} />}
                        </span>
                        <span className={styles.checkText}>
                          <span className={styles.checkTitle}>
                            <Icon size={15} aria-hidden="true" />
                            {label}
                          </span>
                          <span className={styles.checkHint}>{hint}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {filters.dateRange && (
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label htmlFor="sales-start-date" className={styles.label}>
                      Start date
                    </label>
                    <input
                      id="sales-start-date"
                      type="date"
                      name="startDate"
                      className={styles.input}
                      value={selectedFilters.dateRange.startDate}
                      onChange={handleDateChange}
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="sales-end-date" className={styles.label}>
                      End date
                    </label>
                    <input
                      id="sales-end-date"
                      type="date"
                      name="endDate"
                      className={styles.input}
                      value={selectedFilters.dateRange.endDate}
                      onChange={handleDateChange}
                    />
                  </div>
                </div>
              )}

              {filters.school && (
                <div className={styles.field}>
                  <label htmlFor="sales-school" className={styles.label}>
                    School code
                  </label>
                  <div className={styles.selectWrap}>
                    <School size={16} className={styles.selectIcon} aria-hidden="true" />
                    <select
                      id="sales-school"
                      name="school"
                      className={styles.select}
                      value={selectedFilters.school}
                      onChange={handleDropdownChange}
                    >
                      <option value="">Select School</option>
                      {schools.map((school, index) => (
                        <option key={index} value={school}>{school}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {filters.item && (
                <div className={styles.field}>
                  <label htmlFor="sales-item" className={styles.label}>
                    Item code
                  </label>
                  <div className={styles.selectWrap}>
                    <Shirt size={16} className={styles.selectIcon} aria-hidden="true" />
                    <select
                      id="sales-item"
                      name="item"
                      className={styles.select}
                      value={selectedFilters.item}
                      onChange={handleDropdownChange}
                    >
                      <option value="">Select Item</option>
                      {(selectedFilters.school ? filteredItems : items).map((item, index) => (
                        <option key={index} value={item}>{item}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className={`${styles.scopeNote} ${styles.scopeNoteWarn}`} role="alert">
                  <TriangleAlert size={16} aria-hidden="true" />
                  <span>{errorMessage}</span>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={closeFilters}>
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleApplyFilters}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className={styles.btnSpinner} aria-hidden="true" />
                    Generating…
                  </>
                ) : noneTicked ? (
                  'Load all sales'
                ) : (
                  'Apply filters'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── NEW: Export Modal ─────────────────────────────────────────────── */}
      <SalesExportModal
        isOpen={showExportModal}
        onClose={() => {
          setShowExportModal(false);
          setActiveTab('view');
        }}
      />
      {/* ──────────────────────────────────────────────────────────────────── */}

      <div className={styles.page}>
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <span className={styles.headerIcon} aria-hidden="true">
              <ChartColumn size={20} />
            </span>
            <h1 className={styles.title}>Sales Report</h1>
            <div className={styles.chips}>
              {hasDateChip && (
                <span className={styles.chip}>
                  <CalendarRange size={13} aria-hidden="true" />
                  {formatDateForDisplay(selectedFilters.dateRange.startDate)} → {formatDateForDisplay(selectedFilters.dateRange.endDate)}
                </span>
              )}
              {hasSchoolChip && (
                <span className={styles.chip}>
                  <School size={13} aria-hidden="true" />
                  {selectedFilters.school}
                </span>
              )}
              {hasItemChip && (
                <span className={styles.chip}>
                  <Shirt size={13} aria-hidden="true" />
                  {selectedFilters.item}
                </span>
              )}
              {activeFilterCount === 0 &&
                (hasSearched ? (
                  <span className={styles.chip}>All sales</span>
                ) : (
                  <span className={styles.chipMuted}>No report loaded</span>
                ))}
            </div>
          </div>

          <div className={styles.headerActions}>
            {salesData.length > 0 && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={() => fetchSalesData()}
                disabled={loading}
              >
                <RefreshCw size={15} className={loading ? styles.spin : undefined} aria-hidden="true" />
                Refresh
              </button>
            )}
            <button
              type="button"
              className={`${styles.btn} ${styles.btnGhost} ${activeTab === 'export' ? styles.btnGhostOn : ''}`}
              onClick={() => {
                setActiveTab('export');
                setShowExportModal(true);
              }}
            >
              <Download size={15} aria-hidden="true" />
              Export
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => setShowFilterModal(true)}
            >
              <SlidersHorizontal size={15} aria-hidden="true" />
              Filters
              {activeFilterCount > 0 && <span className={styles.filterCount}>{activeFilterCount}</span>}
            </button>
          </div>
        </header>

        {salesData.length > 0 ? (
          <SalesReport data={salesData} loading={loading} />
        ) : (
          <div className={styles.emptyState}>
            {loading ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                <p className={styles.emptyTitle}>Loading sales…</p>
              </>
            ) : (
              <>
                <span className={styles.emptyIcon} aria-hidden="true">
                  <PackageSearch size={28} />
                </span>
                <p className={styles.emptyTitle}>
                  {hasSearched ? 'No sales found' : 'Choose what to view'}
                </p>
                <p className={styles.emptyText}>
                  {hasSearched
                    ? 'Nothing was sold for these filters. Try a different date range, school or item.'
                    : 'Open Filters and tick Date range, School, Item or any mix.'}
                </p>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.emptyAction}`}
                  onClick={() => setShowFilterModal(true)}
                >
                  <SlidersHorizontal size={15} aria-hidden="true" />
                  {hasSearched ? 'Change filters' : 'Open filters'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default FilterSalesPage;
